import { SelectionChange, SelectionModel } from '@angular/cdk/collections';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError, finalize, takeUntil, switchMap } from 'rxjs/operators';
import { LegalizationService } from 'src/app/data/legalization.service';
import { Filter } from 'src/app/models/common/filter';
import { AllLegalizationGridDto } from 'src/app/models/dtos/all-legalization-grid-dto';
import { IPagedResult } from 'src/app/shared/models/PagedResult';
import { GridTableDataSource } from 'src/app/virtual-scroll/data-source';

interface PageInfo {
    page: number;
    count: number;
    obsolete: boolean;
    fetched: boolean;
}

export class LegalizationsListGridDataSource extends GridTableDataSource<AllLegalizationGridDto> {
    legalizationsSubject = new BehaviorSubject<any>(null);
    isLoading$ = new BehaviorSubject<boolean>(false);
    selection = new SelectionModel<AllLegalizationGridDto>(true, []);
    private dataSubject = new BehaviorSubject<AllLegalizationGridDto[]>([]);
    public data$ = this.dataSubject.asObservable();
    private cancelationSubject = new Subject();
    private httpCancelSubject = new Subject();
    private countSubject = new BehaviorSubject<number>(0);
    private _absoluteDataLength: number;
    private pageInfos = new Map<number, PageInfo>()

    count$ = this.countSubject.asObservable();

    get isLoading() {
        return this.isLoading$.getValue();
    }

    get absoluteDataLength() {
        return this._absoluteDataLength;
    }

    public get isAllSelected() {
        return this.allData?.length && this.selection.selected?.length === this.allData.length;
    }

    public get selectionChange$(): Observable<SelectionChange<AllLegalizationGridDto>> {
        return this.selection.changed.asObservable().pipe(takeUntil(this.cancelationSubject));
    }

    public isSelected(item: AllLegalizationGridDto): boolean {
        return this.selection.isSelected(item);
    }

    public selectAll(): void {
        this.selection.select(...this.allData);
    }

    public deselectAll(): void {
        this.selection.clear();
    }

    public setAllPagesAsObsolete() {
        this.pageInfos.forEach(page => page.obsolete = true);
    }

    constructor(
        private legalizationService: LegalizationService,
        { viewport }: { viewport?: CdkVirtualScrollViewport } = {}
    ) {
        super([], { viewport });

        this.legalizationsSubject.pipe(
            takeUntil(this.cancelationSubject),
            switchMap(params => {
                if (params) {
                    return of(this.fetch(params.Page, params.PageSize, params.SortingField, params.SortingDirection, params.Filters));
                }
                return of(null);
            })
        ).subscribe();
    }

    disconnect() {
        this.cancelationSubject.next(undefined);
        this.httpCancelSubject.next(undefined);
        this.isLoading$.next(false);
    }

    reset() {
        this.httpCancelSubject.next(undefined);
        this.httpCancelSubject = new Subject();
        this.isLoading$.next(false);
        this.deselectAll();
        this.allData = [];
        this.pageInfos.clear();
        this.dataSubject.next([]);
    }

  fetch(page: number, count: number, sortingField: string, sortingDirection: string, filters: Filter[]) {
    let pageInfo: PageInfo;

    if (this.pageInfos.has(page)) {
      pageInfo = this.pageInfos.get(page);
      
      if (pageInfo.fetched && !pageInfo.obsolete) {
        return;
      }
    }

    if (!pageInfo) {
      pageInfo = { page, count, fetched: false, obsolete: false };
      this.pageInfos.set(page, pageInfo);
    }

    this.isLoading$.next(true);
    pageInfo.fetched = false;

    this.legalizationService
      .getAllLegalizationGrid(page, count, sortingField, sortingDirection, filters)
      .pipe(
        takeUntil(this.httpCancelSubject),
        catchError((error) => {
          return of({ Results: [], Count: 0 });
        }),
        finalize(() => this.isLoading$.next(false)),
      )
      .subscribe((response: IPagedResult<AllLegalizationGridDto>) => {
        this._absoluteDataLength = response.Count;
        const isAllSelected = this.isAllSelected;
                
        if (response.Results.length > 0) {
          this.merge(response.Results, response.Count);
          this.keepSelectionState(isAllSelected);
          this.countSubject.next(response.Count);
          
          pageInfo.fetched = true;
          pageInfo.obsolete = false;
        } else {
          if (response.Count > 0 && this.allData.length < response.Count) {
            pageInfo.fetched = false;
          } else {
            this.pageInfos.delete(page);
          }
        }
        
        if (response.Count === 0) {
          this.pageInfos.clear();
        }
      });
  }

  private getUniqueId(item: AllLegalizationGridDto): string {
    return `${item.LegalizationId}-${item.DocumentId || 'null'}`;
  }

    private keepSelectionState(isAllSelected: boolean) {
        let old = [...this.selection.selected];
        this.selection.clear();
        this.allData?.forEach(a => (isAllSelected || old.some((r) => 
            r.LegalizationId === a.LegalizationId && 
            r.DocumentId === a.DocumentId)) && 
            this.selection.select(a));
    };

    private merge(data: AllLegalizationGridDto[], absoluteDataLength: number) {
        if (absoluteDataLength <= 0) {
          this.allData = [];
          this.dataSubject.next([]);
          return;
        }

        const isBackgroundRefresh = this.pageInfos.size > 0 && Array.from(this.pageInfos.values()).every(p => p.obsolete);

        if (isBackgroundRefresh) {
            this.allData = data;
            this.dataSubject.next(this.allData);
            return;
        }

        const existingItems = new Map<string, AllLegalizationGridDto>();
        this.allData.forEach(item => {
          existingItems.set(this.getUniqueId(item), item);
        });

        data.forEach(item => {
          const id = this.getUniqueId(item);
          existingItems.set(id, item);
        });

        this.allData = Array.from(existingItems.values());
        this.dataSubject.next(this.allData);
    }
}
